import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, apiFetch } from "@/lib/api";
import { toLocalISODate } from "@/lib/dateUtils";

const money = (n) => `${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const SupplierAgingReport = ({ open, onOpenChange }) => {
  const [asOf, setAsOf] = useState(() => toLocalISODate(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/reports/supplier-aging?as_of=${d}`);
      const j = await res.json();
      setData(j);
    } catch (e) {
      toast.error("Failed to load aging report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(asOf);
  }, [open]);

  const printReport = () => {
    if (!data) return;
    const rows = data.rows.map((r) => `
      <tr>
        <td style="border:1px solid #999;padding:6px 10px">${r.supplier_name}</td>
        <td style="border:1px solid #999;padding:6px 10px">${r.location || ""}</td>
        <td style="border:1px solid #999;padding:6px 10px">${r.po_number || ""}</td>
        <td style="border:1px solid #999;padding:6px 10px;text-align:right">${money(r.amount)}</td>
        <td style="border:1px solid #999;padding:6px 10px;text-align:center">${fmtDate(r.bill_date)}</td>
        <td style="border:1px solid #999;padding:6px 10px;text-align:center">${(r.ageing_days).toFixed(2)}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><title>Supplier Aging Report</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#111}
      table{border-collapse:collapse;width:100%;font-size:13px}
      th{background:#cfe0f5;border:1px solid #999;padding:7px 10px}
      .title{background:#cfe0f5;border:1px solid #999;padding:8px;text-align:center;font-weight:bold;font-size:15px}
      .total td{background:#cfe0f5;font-weight:bold;border:1px solid #999;padding:7px 10px}
      .date{text-align:right;font-size:12px;margin-bottom:6px}</style></head>
      <body>
        <div class="date">${fmtDate(data.as_of)}</div>
        <table>
          <tr><td class="title" colspan="6">GARAGE - SUPPLIER PAYABLES (AGING)</td></tr>
          <tr><th style="text-align:left">SUPPLIER NAME</th><th style="text-align:left">LOCATION</th><th style="text-align:left">PO NUMBER</th><th style="text-align:right">AMOUNT</th><th>BILL DATE</th><th>AGEING DAY</th></tr>
          ${rows || '<tr><td colspan="6" style="border:1px solid #999;padding:14px;text-align:center;color:#666">No outstanding purchase orders</td></tr>'}
          <tr class="total"><td colspan="3" style="text-align:center">TOTAL PAYABLES</td><td style="text-align:right">${money(data.total)}</td><td></td><td></td></tr>
        </table>
      </body></html>`;
    const w = window.open("", "_blank", "width=1000,height=700");
    if (!w) { toast.error("Pop-up blocked. Allow pop-ups to print."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="supplier-aging-dialog">
        <DialogHeader>
          <DialogTitle>Supplier Aging Report (Payables)</DialogTitle>
          <DialogDescription>Outstanding purchase orders by supplier with bill date and ageing days.</DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs">As of date</Label>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="aging-asof" className="w-44" />
          </div>
          <Button variant="outline" onClick={() => load(asOf)} data-testid="aging-apply">Apply</Button>
          <Button onClick={printReport} disabled={!data} data-testid="aging-print">Print / Save PDF</Button>
        </div>

        {/* Aging buckets summary */}
        {data && (
          <div className="grid grid-cols-4 gap-2" data-testid="aging-buckets">
            {[["0-30", "Current (0-30)"], ["31-60", "31-60 Days"], ["61-90", "61-90 Days"], ["90+", "90+ Days"]].map(([k, label]) => (
              <div key={k} className="rounded-md border p-3 text-center">
                <div className="text-xs text-gray-500">{label}</div>
                <div className="text-lg font-bold">₹{money(data.buckets?.[k] || 0)}</div>
              </div>
            ))}
          </div>
        )}

        <div className="border rounded-md overflow-hidden">
          <div className="bg-blue-100 text-center font-semibold py-2 text-sm border-b">GARAGE — SUPPLIER PAYABLES (AGING)</div>
          <div className="grid grid-cols-12 bg-blue-50 text-xs font-semibold px-3 py-2 border-b">
            <span className="col-span-3">SUPPLIER NAME</span>
            <span className="col-span-2">LOCATION</span>
            <span className="col-span-3">PO NUMBER</span>
            <span className="col-span-2 text-right">AMOUNT</span>
            <span className="col-span-1 text-center">BILL DATE</span>
            <span className="col-span-1 text-center">AGEING</span>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
          ) : !data || data.rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="aging-empty">No outstanding purchase orders.</p>
          ) : (
            data.rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 px-3 py-2 text-sm border-t" data-testid="aging-row">
                <span className="col-span-3 font-medium">{r.supplier_name}</span>
                <span className="col-span-2 text-gray-600">{r.location || "—"}</span>
                <span className="col-span-3 text-gray-600">{r.po_number}</span>
                <span className="col-span-2 text-right">₹{money(r.amount)}</span>
                <span className="col-span-1 text-center">{fmtDate(r.bill_date)}</span>
                <span className="col-span-1 text-center font-semibold">{r.ageing_days}</span>
              </div>
            ))
          )}
          {data && (
            <div className="grid grid-cols-12 px-3 py-2 text-sm border-t bg-blue-100 font-bold">
              <span className="col-span-8">TOTAL PAYABLES</span>
              <span className="col-span-2 text-right" data-testid="aging-total">₹{money(data.total)}</span>
              <span className="col-span-2"></span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierAgingReport;
